import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { api } from '../lib/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatAnalytics = () => {
  const [data, setData] = useState(null);
  const [cacheData, setCacheData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [days, setDays] = useState(0); // 0 = all time
  const [scope, setScope] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = { limit: 200 };
      if (days) params.days = days;
      if (scope) params.scope = scope;
      if (userEmail) params.user_email = userEmail;
      const [analyticsRes, cacheRes] = await Promise.all([
        api.get('/api/chat/analytics', { params }),
        api.get('/api/chat/cache/stats').catch(() => ({ data: null })),
      ]);
      setData(analyticsRes.data);
      setCacheData(cacheRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const flushCache = async () => {
    if (!window.confirm('Flush the entire Q+A cache?\n\nAll cached answers will be deleted. The next time each question is asked, it will hit Claude fresh.')) return;
    try {
      await api.delete('/api/chat/cache');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Flush failed');
    }
  };

  const deleteFingerprint = async (fp) => {
    if (!fp) return;
    try {
      await api.delete('/api/chat/cache', { params: { fingerprint: fp } });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const downloadCsv = () => {
    const token = localStorage.getItem('token');
    const qs = days ? `?days=${days}` : '';
    fetch(`${API_URL}/api/chat/analytics/export.csv${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          setError(r.status === 401 ? 'Session expired — please log in again.' : `Export failed (${r.status})`);
          return null;
        }
        return r.blob();
      })
      .then((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat_log_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Network error during export.'));
  };

  return (
    <div className="min-h-screen" data-testid="chat-analytics">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link to="/developer" data-testid="back-to-developer" className="text-sm font-semibold hover:underline"
                  style={{ color: 'var(--unthsc-blue)' }}>
              ← Back to Developer Console
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">Chat Analytics</h1>
            <p className="text-gray-500 mt-1">
              Every student prompt + AI reply is stored with the scope (Topic/Course/Claude),
              module, topic, and timestamp. Use this to improve content over time.
            </p>
          </div>
          <button onClick={downloadCsv} className="btn-secondary text-sm" data-testid="export-csv-btn">
            Download CSV
          </button>
        </div>

        {/* Filters */}
        <section className="card p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Time window</label>
              <select className="input-field" value={days} onChange={(e) => setDays(Number(e.target.value))}
                      data-testid="filter-days">
                <option value={0}>All time</option>
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Scope</label>
              <select className="input-field" value={scope} onChange={(e) => setScope(e.target.value)}
                      data-testid="filter-scope">
                <option value="">All scopes</option>
                <option value="topic">📍 Topic</option>
                <option value="course">📚 Course</option>
                <option value="training">🌐 Claude training</option>
              </select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-semibold text-gray-700 mb-1">User email contains</label>
              <input className="input-field" value={userEmail} onChange={(e) => setUserEmail(e.target.value)}
                     placeholder="exact email" data-testid="filter-email" />
            </div>
            <button onClick={load} className="btn-primary text-sm" data-testid="apply-filters-btn">
              Apply
            </button>
          </div>
        </section>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>}

        {/* Stats */}
        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : data ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <StatCard title="Total messages" value={data.total_messages} />
              <StatCard title="By scope (student questions)"
                rows={data.by_scope.map((s) => [s.scope, s.count])} />
              <StatCard title="Top users (student questions)"
                rows={data.by_user.slice(0, 5).map((u) => [u.email, u.count])} />
            </div>

            {/* Q+A Cache panel */}
            <section className="card p-4 mb-6" data-testid="cache-faqs">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <h2 className="font-bold text-gray-900 flex items-center gap-2">
                    ⚡ Cached FAQs
                    {cacheData && (
                      <span className="text-xs font-normal text-gray-500">
                        {cacheData.total_cached_questions} cached · {cacheData.total_cache_hits} hits saved
                      </span>
                    )}
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Questions where students asked the same thing on the same day. Each hit = one Claude call skipped.
                    Entries auto-expire after 365 days. Matches via <b>exact text</b> OR <b>semantic similarity ≥ 0.82</b> (paraphrases like
                    "What is X?" / "Explain X" / "Can you explain X" all hit the same entry).
                  </p>
                </div>
                {cacheData && cacheData.total_cached_questions > 0 && (
                  <button onClick={flushCache} className="btn-outline text-xs" data-testid="flush-cache-btn">
                    Flush cache
                  </button>
                )}
              </div>

              {!cacheData ? (
                <div className="text-gray-400 text-sm">No cache data.</div>
              ) : cacheData.total_cached_questions === 0 ? (
                <div className="text-gray-400 text-sm italic" data-testid="cache-empty">
                  No questions cached yet. The cache fills as students ask their first questions.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-600 border-b border-gray-200">
                      <tr>
                        <th className="py-2 pr-3 text-xs">Hits</th>
                        <th className="py-2 pr-3 text-xs">Question</th>
                        <th className="py-2 pr-3 text-xs">Scope</th>
                        <th className="py-2 pr-3 text-xs">Module / Topic</th>
                        <th className="py-2 pr-3 text-xs">First asked</th>
                        <th className="py-2 pr-3 text-xs">Last used</th>
                        <th className="py-2 text-xs"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cacheData.top_questions.map((q, i) => (
                        <tr key={q.fingerprint || i} className="border-b border-gray-100 align-top"
                            data-testid={`cache-row-${i}`}>
                          <td className="py-2 pr-3">
                            <span className="inline-flex items-center justify-center min-w-[32px] px-1.5 py-0.5 rounded text-xs font-bold"
                                  style={{ background: q.hit_count > 0 ? '#dcfce7' : '#f3f4f6',
                                           color: q.hit_count > 0 ? '#166534' : '#6b7280' }}>
                              {q.hit_count}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-gray-800 max-w-md">
                            <div className="font-medium text-sm">{q.question_original}</div>
                            <details className="mt-1">
                              <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-700">View cached answer</summary>
                              <div className="mt-1 p-2 bg-gray-50 rounded text-xs text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap">
                                {(q.answer || '').slice(0, 1500)}{(q.answer || '').length > 1500 ? '…' : ''}
                              </div>
                            </details>
                          </td>
                          <td className="py-2 pr-3 text-xs">{q.scope || '—'}</td>
                          <td className="py-2 pr-3 text-xs text-gray-600">
                            {q.module_id || '—'}<br/>{(q.topic_id || '').slice(0, 18) || '—'}
                          </td>
                          <td className="py-2 pr-3 text-xs text-gray-500 whitespace-nowrap">
                            <div>{(q.first_asked_at || '').replace('T', ' ').slice(0, 16)}</div>
                            <div className="text-gray-400">by {q.first_asked_by}</div>
                          </td>
                          <td className="py-2 pr-3 text-xs text-gray-500 whitespace-nowrap">
                            {(q.last_used_at || '').replace('T', ' ').slice(0, 16)}
                          </td>
                          <td className="py-2">
                            <button onClick={() => deleteFingerprint(q.fingerprint)}
                                    className="text-xs text-red-600 hover:text-red-700 font-bold px-1"
                                    title="Delete this entry"
                                    data-testid={`cache-delete-${i}`}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Recent messages table */}
            <section className="card p-4">
              <h2 className="font-bold text-gray-900 mb-3">Recent messages ({data.messages.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-600 border-b border-gray-200">
                    <tr>
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">User</th>
                      <th className="py-2 pr-3">Role</th>
                      <th className="py-2 pr-3">Scope</th>
                      <th className="py-2 pr-3">Module / Topic</th>
                      <th className="py-2">Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.messages.map((m, i) => (
                      <tr key={i} className="border-b border-gray-100 align-top">
                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap text-xs">
                          {m.created_at?.replace('T', ' ').slice(0, 19)}
                        </td>
                        <td className="py-2 pr-3 text-xs">{m.user_email}</td>
                        <td className="py-2 pr-3">
                          <span className={m.role === 'user' ? 'badge-published' : 'badge-draft'}>
                            {m.role}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs">{m.scope || '—'}</td>
                        <td className="py-2 pr-3 text-xs text-gray-600">
                          {(m.module_id || '—')}<br/>{(m.topic_id || '').slice(0, 18) || '—'}
                        </td>
                        <td className="py-2 text-gray-800 text-xs max-w-2xl">
                          {(m.content || '').slice(0, 400)}
                          {(m.content || '').length > 400 ? '…' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
};

const StatCard = ({ title, value, rows }) => (
  <div className="card p-4">
    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{title}</div>
    {value !== undefined && (
      <div className="text-3xl font-black" style={{ color: 'var(--unthsc-green-dark)' }}>{value}</div>
    )}
    {rows && (
      <ul className="text-sm space-y-1">
        {rows.length === 0 && <li className="text-gray-400 italic">No data</li>}
        {rows.map(([k, v], i) => (
          <li key={i} className="flex justify-between gap-2">
            <span className="truncate">{k}</span>
            <span className="font-semibold">{v}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default ChatAnalytics;
